/**
 * Post-signup gate (shown to users who aren't yet approved).
 *
 * Unified with the pricing model: there are no legacy plan tiers here. A user
 * either joins the early-access waitlist (free) or, for a team / bulk need,
 * contacts us. Approval flips them into the app (the effect below bounces them
 * in the moment an admin approves).
 */

import { useEffect, useState } from 'react';
import { CheckCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';
import { Button } from '../ui/button';
import Logo from '../ui/logo';
import { EnterpriseContactDialog } from '../billing/EnterpriseContactDialog';
import { freeCreditsSentence } from '../../config/pricing';

export const WaitlistPage = () => {
  const { user, signOut, isApproved } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [onWaitlist, setOnWaitlist] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  // Prefer Firebase displayName ("Rahul Sharma" from Google SSO) → first name.
  // Fallback: email prefix, skipping two-char initials like "ra." in "ra.rahulsharma.sh@...".
  const firstName: string =
    (user as any)?.displayName?.toString().split(' ')[0] ||
    user?.email?.split('@')[0]?.split(/[._-]/).find((s: string) => s.length > 2) ||
    user?.email?.split('@')[0] ||
    '';
  const greeting = firstName ? `Welcome, ${firstName.charAt(0).toUpperCase()}${firstName.slice(1)}` : 'Welcome';

  // If admin approves while the user is on this page, bounce them into the app.
  useEffect(() => {
    if (isApproved) window.location.href = '/';
  }, [isApproved]);

  async function joinWaitlist() {
    if (!user?.email) {
      setError('No email on file.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post('/billing/waitlist', {
        email: user.email,
        planInterest: 'general', // generic interest marker (internal only)
        source: 'signup_gate',
      });
      setOnWaitlist(true);
    } catch (err: any) {
      const raw = err?.response?.data?.error;
      const msg = typeof raw === 'string' ? raw
        : raw && typeof raw === 'object' ? (raw.message || JSON.stringify(raw))
        : 'Could not join waitlist.';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <Logo variant="modern" />
          </div>
          <h1 className="text-3xl font-semibold tracking-tight mb-2">{greeting}</h1>
          <p className="text-lg text-muted-foreground">
            We're onboarding new creators in batches
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded-xl border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <div className="rounded-2xl border border-border bg-card p-6">
          {onWaitlist ? (
            <div className="flex flex-col items-center justify-center text-center gap-3 py-4">
              <CheckCircle className="w-10 h-10 text-primary" />
              <div>
                <p className="font-medium">You're on the list</p>
                <p className="text-sm text-muted-foreground">
                  We'll email <span className="font-mono">{user?.email}</span> the moment your access is ready.
                </p>
              </div>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-bold mb-1">Join the early-access list</h2>
              <p className="text-sm text-muted-foreground mb-5">
                {freeCreditsSentence()} Then pay only for what you make. We'll let you in as soon as a spot
                opens.
              </p>
              <Button className="w-full" size="lg" onClick={joinWaitlist} disabled={busy}>
                {busy ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Adding…</>
                ) : (
                  'Keep me posted'
                )}
              </Button>
              <p className="text-xs text-muted-foreground text-center mt-2">
                Zero spam. One email when you're in.
              </p>
            </>
          )}
        </div>

        <div className="text-center mt-6 text-sm text-muted-foreground">
          Working with a team or need bulk access?{' '}
          <button
            type="button"
            onClick={() => setContactOpen(true)}
            className="text-primary hover:underline font-medium"
          >
            Contact us
          </button>
        </div>

        {user && (
          <div className="text-center mt-6">
            <Button variant="ghost" size="sm" onClick={async () => { await signOut(); window.location.href = '/login'; }}>
              Sign out
            </Button>
          </div>
        )}
      </div>

      <EnterpriseContactDialog open={contactOpen} onOpenChange={setContactOpen} source="signup_gate" />
    </div>
  );
};
